import React, { useState } from 'react';
import { useAuthenticatedFetch } from '../../api/client';
import {
  Modal,
  Input,
  Button,
  Stack,
  Flex,
  useTheme,
} from '@fti/design-system';

interface CreateProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export const CreateProjectModal: React.FC<CreateProjectModalProps> = ({ isOpen, onClose, onCreated }) => {
  const { theme } = useTheme();
  const { fetchData } = useAuthenticatedFetch();
  const [name, setName] = useState('');
  const [matterCode, setMatterCode] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError('Project name is required');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await fetchData('/dastarkhan/projects', {
        method: 'POST',
        body: JSON.stringify({
          name: name.trim(),
          matter_code: matterCode.trim() || undefined,
          description: description.trim() || undefined,
        }),
      });
      setName('');
      setMatterCode('');
      setDescription('');
      onCreated();
      onClose();
    } catch {
      setError('Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <Modal.Header onClose={onClose}>Create Investigation Project</Modal.Header>
      <Modal.Body>
        <Stack gap="md">
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '4px', color: theme.colors.text.primary }}>
              Project Name *
            </label>
            <Input
              value={name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
              placeholder="e.g., Operation Crosswind"
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '4px', color: theme.colors.text.primary }}>
              Matter Code
            </label>
            <Input
              value={matterCode}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMatterCode(e.target.value)}
              placeholder="e.g., FTI-2026-0042"
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '4px', color: theme.colors.text.primary }}>
              Description
            </label>
            <Input
              value={description}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)}
              placeholder="Brief description of the investigation"
            />
          </div>
          {error && (
            <div style={{ color: theme.colors.semantic.error.default, fontSize: '0.85rem' }}>
              {error}
            </div>
          )}
        </Stack>
      </Modal.Body>
      <Modal.Footer>
        <Flex justify="end" gap="sm">
          <Button variant="outline" size="sm" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="solid" color="primary" size="sm" onClick={handleSubmit} disabled={loading}>
            {loading ? 'Creating...' : 'Create Project'}
          </Button>
        </Flex>
      </Modal.Footer>
    </Modal>
  );
};
